import api from '@/services/Api';
import moment from 'moment';
import Impferinnerung from '@/types/Impferinnerung';
import { successSwal, toastSwal } from '@/helper/swals';
import { ActionContext, Module } from 'vuex';
import { AxiosResponse } from 'axios';
import Impfung from '@/types/Impfung';
import Patient from '@/types/Patient';
import Karteieintrag from '@/types/Karteieintrag';
import { Satzart } from '@/types/Satzart';
import {
    ImpferinnerungenFilter,
    ImpferinnerungenState,
    RootState
} from '@/store/types';
import { DataResponse } from '@/types';
import PrintManager from '@/services/PrintManager';
import { saveAs } from 'file-saver';

export type LoadImpferinnerungenPayload = { filter: ImpferinnerungenFilter };
export type OpenKarteiPayload = { patimpfung_id: number; patnr: number };
export type LoadKarteiImpfungenPayload = { filter: { satzarten: number[] } };
export type OpenBriefPayload = { patimpfung_id: number };
export type PrintBriefPayload = { briefe: number[]; text: string | null, export: boolean, name: string };
export type PrintBriefePayload = { briefe: number[]; };
export type SendEmailPayload = { briefe: number[]; };
export type SendSmsPayload = { briefe: number[]; };
export type AlsErinnertMarkierenPayload = { briefe: number[]; filter: ImpferinnerungenFilter };
export type SetImpferinnerungenDialogs = { kartei: boolean; brief: boolean };
export type SetImpferinnerungenKarteiLoaders = { loadingId?: number | null; impfungen?: boolean };
export type SetImpferinnerungenBriefLoaders = { loadingId?: number | null; print?: boolean };
export type SetImpferinnerungenLoaders = {
    print?: boolean;
    email?: boolean;
    sms?: boolean;
    mahnung?: boolean;
    impferinnerungen?: boolean;
};

type LoadImpferinnerungenData = DataResponse<Partial<Impferinnerung>[], {
    pagination: {
        page: number;
        limit: number;
        order: string[];
        count: number;
    };
}>;

type Context = ActionContext<ImpferinnerungenState, RootState>;

export const impferinnerungen: Module<ImpferinnerungenState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            kartei: false,
            brief: false,
        },
        loaders: {
            kartei: {
                loadingId: null,
                impfungen: false,
            },
            brief: {
                loadingId: null,
                print: false,
                export: false,
            },
            print: false,
            email: false,
            sms: false,
            mahnung: false,
            impferinnerungen: false,
        },
        impferinnerungen: [],
        totalCount: 0,
        selectedItems: [],
        filter: {
            faelligBis: moment().format('YYYY-MM-DD'),
            email: 'n',
            sms: 'n',
            pagination: {
                page: 1,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 10,
                pageCount: 10,
                itemsLength: 25,
            },
        },
        kartei: {
            patient: new Patient(),
            impfungen: [],
            filter: {
                satzarten: [Satzart.Impfung],
            },
        },
        brief: new Impfung(),
    },
    actions: {
        loadImpferinnerungen({ commit }: Context, payload?: LoadImpferinnerungenPayload): void {
            commit('setLoaders', { impferinnerungen: true });

            api.post('impferinnerungen/impferinnerungen/load', payload)
                .then((response: AxiosResponse) => {
                    commit('setSelectedItems', []);
                    commit('setImpferinnerungen', response.data);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { impferinnerungen: false }));
        },
        openKartei({ state, commit }: Context, { patimpfung_id, patnr }: OpenKarteiPayload): void {
            commit('setLoadersKartei', { loadingId: patimpfung_id });

            api.post('impferinnerungen/impferinnerungen/kartei', { patnr, filter: state.kartei.filter })
                .then((response: AxiosResponse) => {
                    commit('setKartei', response.data.body);
                    commit('setDialogs', { kartei: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoadersKartei', { loadingId: null }));
        },
        loadKarteiImpfungen({ state, commit }: Context, payload: LoadKarteiImpfungenPayload): void {
            commit('setLoadersKartei', { impfungen: true });

            api.post('impferinnerungen/impferinnerungen/kartei', { patnr: state.kartei.patient.patnr, ...payload })
                .then((response: AxiosResponse) => commit('setKarteiImpfungen', response.data.body.impfungen))
                .catch(console.error)
                .finally(() => commit('setLoadersKartei', { impfungen: false }));
        },
        openBrief({ commit }: Context, { patimpfung_id }: OpenBriefPayload): void {
            commit('setLoadersBrief', { loadingId: patimpfung_id });

            api.post('impferinnerungen/impferinnerungsbrief/load', { patimpfung_id })
                .then((response: AxiosResponse) => {
                    commit('setBrief', response.data.body);
                    commit('setDialogs', { brief: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoadersBrief', { loadingId: null }));
        },
        printBrief({ commit, dispatch, rootState }: Context, payload: PrintBriefPayload): void {
            const isExport = payload.export;
            const patientName = payload.name;

            commit('setLoadersBrief', { [isExport ? 'export' : 'print']: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;
            const data = { impfstelle_id, ...payload };

            api.post('impferinnerungen/impferinnerungsbrief/index', data, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                    if (isExport) {
                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        saveAs(data, `Impferinnerungsbrief_${patientName}_${date}.pdf`);
                    } else {
                        dispatch('globals/printDokument', { data }, { root: true });
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersBrief', { [isExport ? 'export' : 'print']: false }));
        },
        printBriefe({ commit, dispatch, rootState }: Context, payload: PrintBriefePayload): void {
            commit('setLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;
            const data = { impfstelle_id, ...payload };
            const duplex = PrintManager.printer?.manager?.DuplexMode?.Simplex;

            api.post('impferinnerungen/impferinnerungsbrief/index', data, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                    //commit('setSelectedItems', []);
                    dispatch('globals/printDokument', { data, duplex }, { root: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { print: false }));
        },
        closeBrief({ commit }: Context): void {
            commit('setDialogs', { brief: false });
            commit('setBrief', null);
        },
        sendEmail({ commit }: Context, payload: SendEmailPayload): void {
            commit('setLoaders', { email: true });

            api.post('impferinnerungen/impferinnerungsbrief/email', payload)
                .then((response: AxiosResponse) => {
                    //commit('setSelectedItems', []);

                    const sent = response.data.body;
                    const toSend = payload.briefe.length;
                    const success = sent === toSend;

                    toastSwal(
                        success ? 'success' : 'warning',
                        'Impferinnerungen Emails',
                        `${sent} von ${toSend} gesendet`,
                        success ? 3000 : 6000);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { email: false }));
        },
        sendSms({ commit }: Context, payload: SendSmsPayload): void {
            commit('setLoaders', { sms: true });

            api.post('impferinnerungen/impferinnerungsbrief/sms', payload)
                .then((response: AxiosResponse) => {
                    commit('setSelectedItems', []);

                    const sent = response.data.body;
                    const toSend = payload.briefe.length;
                    const success = sent === toSend;

                    toastSwal(
                        success ? 'success' : 'warning',
                        'Impferinnerungen SMS',
                        `${sent} von ${toSend} gesendet`,
                        success ? 3000 : 6000);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { sms: false }));
        },
        alsErinnertMarkieren({ commit, dispatch }: Context, { briefe, filter }: AlsErinnertMarkierenPayload):
            void {
            commit('setLoaders', { mahnung: true });

            api.post('impferinnerungen/impferinnerungen/erinnert', { briefe })
                .then(() => {
                    commit('setSelectedItems', []);
                    dispatch('loadImpferinnerungen', { filter });
                    successSwal('Impferinnerungen', 'als erinnert markiert');
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { mahnung: false }));
        },
        setDialogs({ commit }: Context, payload: SetImpferinnerungenDialogs): void {
            commit('setDialogs', payload);
        },
        setLoaders({ commit }: Context, payload: SetImpferinnerungenLoaders): void {
            commit('setDialogs', payload);
        },
        setBrief({ commit }: Context, payload: Impfung | null): void {
            commit('setBrief', payload);
        },
        setPage({ commit }: Context, payload: number): void {
            commit('setPage', payload);
        },
        setSelectedItems({ commit }: Context, payload: Impferinnerung[]): void {
            commit('setSelectedItems', payload);
        },
    },
    mutations: {
        setImpferinnerungen(state: ImpferinnerungenState, payload: LoadImpferinnerungenData): void {
            state.impferinnerungen = payload.body.map((data) => new Impferinnerung(data));
            state.totalCount = payload.selections.pagination.count;
        },
        setTotalCount(state: ImpferinnerungenState, totalCount: number): void {
            state.totalCount = totalCount;
        },
        setSelectedItems(state: ImpferinnerungenState, items: Impferinnerung[]): void {
            state.selectedItems = items;
        },
        setKartei(state: ImpferinnerungenState,
                  payload: { patient: Partial<Patient>, impfungen: Partial<Karteieintrag>[] }): void {
            state.kartei.patient = new Patient(payload.patient);
            state.kartei.impfungen = payload.impfungen.map((data) => new Karteieintrag(data));
        },
        setKarteiImpfungen(state: ImpferinnerungenState, payload: Partial<Karteieintrag>[]): void {
            state.kartei.impfungen = payload.map((data) => new Karteieintrag(data));
        },
        setBrief(state: ImpferinnerungenState, payload: Impfung | null): void {
            state.brief = payload;
        },
        setDialogs(state: ImpferinnerungenState, payload: SetImpferinnerungenDialogs): void {
            state.dialogs = {
                ...state.dialogs,
                ...payload,
            };
        },
        setLoaders(state: ImpferinnerungenState, payload?: SetImpferinnerungenLoaders): void {
            state.loaders = {
                ...state.loaders,
                ...payload,
            };
        },
        setLoadersBrief(state: ImpferinnerungenState, payload: SetImpferinnerungenBriefLoaders): void {
            state.loaders.brief = {
                ...state.loaders.brief,
                ...payload,
            };
        },
        setLoadersKartei(state: ImpferinnerungenState, payload: SetImpferinnerungenKarteiLoaders): void {
            state.loaders.kartei = {
                ...state.loaders.kartei,
                ...payload,
            };
        },
    },
};
